package com.sddamico.draftmachine.util

import com.sddamico.draftmachine.api.ApiException
import com.sddamico.draftmachine.api.apiGet
import com.sddamico.draftmachine.api.apiJson
import com.sddamico.draftmachine.api.apiPost
import com.sddamico.draftmachine.models.DraftResponse
import com.sddamico.draftmachine.models.DraftPickNotificationsResponse
import com.sddamico.draftmachine.models.EditPickRequest
import com.sddamico.draftmachine.models.CancelDraftRequest
import com.sddamico.draftmachine.models.LotteryActionRequest
import com.sddamico.draftmachine.models.LotteryConfigEntry
import com.sddamico.draftmachine.models.LotteryStateResponse
import com.sddamico.draftmachine.models.SaveLotteryConfigRequest
import com.sddamico.draftmachine.models.MakePickRequest
import com.sddamico.draftmachine.models.MakePickResponse
import com.sddamico.draftmachine.models.ManagePickRequest
import com.sddamico.draftmachine.models.ManagePickResponse
import com.sddamico.draftmachine.models.PickResponse
import com.sddamico.draftmachine.models.NudgeRequest
import com.sddamico.draftmachine.models.QuietHoursSettings
import com.sddamico.draftmachine.models.StartDraftRequest
import com.sddamico.draftmachine.models.SuccessResponse
import com.sddamico.draftmachine.models.UpdateDraftSettingsRequest
import com.sddamico.draftmachine.models.UpdateDraftPickNotificationsRequest
import com.sddamico.draftmachine.models.SetDraftAdminsRequest
import com.sddamico.draftmachine.models.TeamResponse
import com.sddamico.draftmachine.models.UpdateDraftTeamRequest
import com.sddamico.draftmachine.models.SkipPickRequest
import kotlinx.serialization.builtins.ListSerializer

/** Outcome of [startDraft]: success carries the started draft; failure carries a display message. */
sealed interface StartDraftResult {
  data class Success(val draft: DraftResponse) : StartDraftResult
  data class Failure(val message: String) : StartDraftResult
}

sealed interface CancelDraftResult {
  data class Success(val draft: DraftResponse) : CancelDraftResult
  data class Failure(val message: String) : CancelDraftResult
}

sealed interface UpdateDraftSettingsResult {
  data class Success(val draft: DraftResponse) : UpdateDraftSettingsResult
  data class Failure(val message: String) : UpdateDraftSettingsResult
}

sealed interface UpdateDraftPickNotificationsResult {
  data class Success(val enabled: Boolean) : UpdateDraftPickNotificationsResult
  data class Failure(val message: String) : UpdateDraftPickNotificationsResult
}

sealed interface SetDraftAdminsResult {
  data object Success : SetDraftAdminsResult
  data class Failure(val message: String) : SetDraftAdminsResult
}

suspend fun setDraftAdmins(draftId: String, teamIds: List<String>): SetDraftAdminsResult =
  try {
    apiPost(
      "/api/draft-admins",
      apiJson.encodeToString(SetDraftAdminsRequest.serializer(), SetDraftAdminsRequest(draftId, teamIds)),
      SuccessResponse.serializer(),
    )
    SetDraftAdminsResult.Success
  } catch (e: ApiException) {
    SetDraftAdminsResult.Failure(e.errorMessage)
  } catch (e: Throwable) {
    SetDraftAdminsResult.Failure(e.message ?: UNREACHABLE_MESSAGE)
  }

/** [quietHours] left null keeps the draft's stored overnight pause; [pickTimerMinutes] always applies. */
suspend fun updateDraftSettings(
  draftId: String,
  pickTimerMinutes: Int?,
  quietHours: QuietHoursSettings? = null,
): UpdateDraftSettingsResult =
  try {
    val draft = apiPost(
      "/api/draft-settings",
      apiJson.encodeToString(
        UpdateDraftSettingsRequest.serializer(),
        UpdateDraftSettingsRequest(draftId, pickTimerMinutes, quietHours),
      ),
      DraftResponse.serializer(),
    )
    UpdateDraftSettingsResult.Success(draft)
  } catch (e: ApiException) {
    UpdateDraftSettingsResult.Failure(e.errorMessage)
  } catch (e: Throwable) {
    UpdateDraftSettingsResult.Failure(e.message ?: UNREACHABLE_MESSAGE)
  }

suspend fun updateDraftPickNotifications(
  draftId: String,
  enabled: Boolean,
): UpdateDraftPickNotificationsResult =
  try {
    val response = apiPost(
      "/api/draft-pick-notifications",
      apiJson.encodeToString(
        UpdateDraftPickNotificationsRequest.serializer(),
        UpdateDraftPickNotificationsRequest(draftId, enabled),
      ),
      DraftPickNotificationsResponse.serializer(),
    )
    UpdateDraftPickNotificationsResult.Success(response.enabled)
  } catch (e: ApiException) {
    UpdateDraftPickNotificationsResult.Failure(e.errorMessage)
  } catch (e: Throwable) {
    UpdateDraftPickNotificationsResult.Failure(e.message ?: UNREACHABLE_MESSAGE)
  }

/** Cancels an active draft and normalizes API failures for the confirmation sheet. */
suspend fun cancelDraft(draftId: String): CancelDraftResult =
  try {
    val draft = apiPost(
      "/api/draft-cancel",
      apiJson.encodeToString(CancelDraftRequest.serializer(), CancelDraftRequest(draftId)),
      DraftResponse.serializer(),
    )
    CancelDraftResult.Success(draft)
  } catch (e: ApiException) {
    CancelDraftResult.Failure(e.errorMessage)
  } catch (e: Throwable) {
    CancelDraftResult.Failure(e.message ?: UNREACHABLE_MESSAGE)
  }

/**
 * POSTs `/api/draft-start` for [draftId] and normalizes the error surface (an [ApiException]
 * surfaces its [ApiException.errorMessage]; anything else falls back to [UNREACHABLE_MESSAGE]).
 * UI-free — callers decide how to present [StartDraftResult.Failure.message].
 */
suspend fun startDraft(draftId: String): StartDraftResult =
  try {
    val draft = apiPost(
      "/api/draft-start",
      apiJson.encodeToString(StartDraftRequest.serializer(), StartDraftRequest(draftId)),
      DraftResponse.serializer(),
    )
    StartDraftResult.Success(draft)
  } catch (e: ApiException) {
    StartDraftResult.Failure(e.errorMessage)
  } catch (e: Throwable) {
    StartDraftResult.Failure(e.message ?: UNREACHABLE_MESSAGE)
  }

/** Outcome of [submitPick]: success carries the updated pick + draft status; failure a message. */
sealed interface PickResult {
  data class Success(val response: MakePickResponse) : PickResult
  data class Failure(val message: String) : PickResult
}

sealed interface EditPickResult {
  data class Success(val pick: PickResponse) : EditPickResult
  data class Failure(val message: String) : EditPickResult
}

suspend fun editPick(draftId: String, position: Int, playerText: String): EditPickResult =
  try {
    val pick = apiPost(
      "/api/draft-pick-edit",
      apiJson.encodeToString(
        EditPickRequest.serializer(),
        EditPickRequest(draftId = draftId, position = position, playerText = playerText),
      ),
      PickResponse.serializer(),
    )
    EditPickResult.Success(pick)
  } catch (e: ApiException) {
    EditPickResult.Failure(e.errorMessage)
  } catch (e: Throwable) {
    EditPickResult.Failure(e.message ?: UNREACHABLE_MESSAGE)
  }

/** Outcome of [managePick]: success carries the updated pick + draft status; failure a message. */
sealed interface ManagePickResult {
  data class Success(val response: ManagePickResponse) : ManagePickResult
  data class Failure(val message: String) : ManagePickResult
}

/**
 * POSTs `/api/draft-pick-manage` — the board's manager/admin control over one cell. Each null
 * argument leaves that aspect of the pick alone, so the sheet sends only what it changed.
 * UI-free — callers refresh the board and decide how to present a [ManagePickResult.Failure].
 */
suspend fun managePick(
  draftId: String,
  position: Int,
  playerText: String? = null,
  teamId: String? = null,
  skipped: Boolean? = null,
  overrideOwnership: Boolean = false,
): ManagePickResult =
  try {
    val response = apiPost(
      "/api/draft-pick-manage",
      apiJson.encodeToString(
        ManagePickRequest.serializer(),
        ManagePickRequest(
          draftId = draftId,
          position = position,
          playerText = playerText,
          teamId = teamId,
          skipped = skipped,
          overrideOwnership = overrideOwnership,
        ),
      ),
      ManagePickResponse.serializer(),
    )
    ManagePickResult.Success(response)
  } catch (e: ApiException) {
    ManagePickResult.Failure(e.errorMessage)
  } catch (e: Throwable) {
    ManagePickResult.Failure(e.message ?: UNREACHABLE_MESSAGE)
  }

/**
 * POSTs `/api/draft-pick` with a free-text [playerText] for the pick at ([draftId], [position]).
 * [overrideOwnership] requests the server's administrator-only league ownership override.
 * UI-free — callers refresh the room and decide how to present a [PickResult.Failure].
 */
suspend fun submitPick(
  draftId: String,
  position: Int,
  playerText: String,
  overrideOwnership: Boolean = false,
): PickResult =
  try {
    val response = apiPost(
      "/api/draft-pick",
      apiJson.encodeToString(
        MakePickRequest.serializer(),
        MakePickRequest(
          draftId = draftId,
          position = position,
          playerText = playerText,
          overrideOwnership = overrideOwnership,
        ),
      ),
      MakePickResponse.serializer(),
    )
    PickResult.Success(response)
  } catch (e: ApiException) {
    PickResult.Failure(e.errorMessage)
  } catch (e: Throwable) {
    PickResult.Failure(e.message ?: UNREACHABLE_MESSAGE)
  }

/** Outcome of [skipPick]: success carries the updated pick + draft status; failure a message. */
sealed interface SkipPickResult {
  data class Success(val response: ManagePickResponse) : SkipPickResult
  data class Failure(val message: String) : SkipPickResult
}

/** POSTs `/api/draft-pick-skip` to skip the caller's own on-clock pick. UI-free. */
suspend fun skipPick(draftId: String, position: Int): SkipPickResult =
  try {
    val response = apiPost(
      "/api/draft-pick-skip",
      apiJson.encodeToString(SkipPickRequest.serializer(), SkipPickRequest(draftId, position)),
      ManagePickResponse.serializer(),
    )
    SkipPickResult.Success(response)
  } catch (e: ApiException) {
    SkipPickResult.Failure(e.errorMessage)
  } catch (e: Throwable) {
    SkipPickResult.Failure(e.message ?: UNREACHABLE_MESSAGE)
  }

/**
 * Outcome of [nudgeOnClock]: a success, a friendly rate-limited state (HTTP 429 — the machine
 * needs a minute), or any other failure.
 */
sealed interface NudgeResult {
  data class Success(val message: String) : NudgeResult
  data class RateLimited(val message: String) : NudgeResult
  data class Failure(val message: String) : NudgeResult
}

/** Outcome of a lottery mutation: success carries the masked lottery state; failure a message. */
sealed interface LotteryActionResult {
  data class Success(val state: LotteryStateResponse) : LotteryActionResult
  data class Failure(val message: String) : LotteryActionResult
}

/** POSTs `/api/draft-lottery` to create/replace the lottery config while the draw is uncommitted. */
suspend fun saveLotteryConfig(
  draftId: String,
  lotterySize: Int,
  entries: List<LotteryConfigEntry>,
): LotteryActionResult =
  try {
    val state = apiPost(
      "/api/draft-lottery",
      apiJson.encodeToString(
        SaveLotteryConfigRequest.serializer(),
        SaveLotteryConfigRequest(draftId, lotterySize, entries),
      ),
      LotteryStateResponse.serializer(),
    )
    LotteryActionResult.Success(state)
  } catch (e: ApiException) {
    LotteryActionResult.Failure(e.errorMessage)
  } catch (e: Throwable) {
    LotteryActionResult.Failure(e.message ?: UNREACHABLE_MESSAGE)
  }

/** Reveals the next draw (committing the whole draw server-side on the first call). */
suspend fun spinLottery(draftId: String): LotteryActionResult =
  lotteryAction("/api/draft-lottery-spin", draftId)

/** Reveals every remaining draw at once. */
suspend fun skipLottery(draftId: String): LotteryActionResult =
  lotteryAction("/api/draft-lottery-skip", draftId)

private suspend fun lotteryAction(url: String, draftId: String): LotteryActionResult =
  try {
    val state = apiPost(
      url,
      apiJson.encodeToString(LotteryActionRequest.serializer(), LotteryActionRequest(draftId)),
      LotteryStateResponse.serializer(),
    )
    LotteryActionResult.Success(state)
  } catch (e: ApiException) {
    LotteryActionResult.Failure(e.errorMessage)
  } catch (e: Throwable) {
    LotteryActionResult.Failure(e.message ?: UNREACHABLE_MESSAGE)
  }

/** POSTs `/api/draft-nudge` to re-send the current on-the-clock email. */
suspend fun nudgeOnClock(draftId: String): NudgeResult =
  try {
    val res = apiPost(
      "/api/draft-nudge",
      apiJson.encodeToString(NudgeRequest.serializer(), NudgeRequest(draftId)),
      SuccessResponse.serializer(),
    )
    NudgeResult.Success(res.message)
  } catch (e: ApiException) {
    if (e.status == 429) NudgeResult.RateLimited(e.errorMessage) else NudgeResult.Failure(e.errorMessage)
  } catch (e: Throwable) {
    NudgeResult.Failure(e.message ?: UNREACHABLE_MESSAGE)
  }

/** The draft's teams with owner emails; only a draft administrator may read them. */
suspend fun fetchDraftTeams(draftId: String): List<TeamResponse> =
  apiGet("/api/draft-teams?draftId=$draftId", ListSerializer(TeamResponse.serializer()))

enum class TeamField(val wireName: String) {
  TeamName("teamName"),
  OwnerName("ownerName"),
  OwnerEmail("ownerEmail"),
}

sealed interface UpdateDraftTeamResult {
  data class Success(val team: TeamResponse) : UpdateDraftTeamResult
  data class Invalid(val field: TeamField, val message: String) : UpdateDraftTeamResult
  data class Failure(val message: String) : UpdateDraftTeamResult
}

suspend fun updateDraftTeam(request: UpdateDraftTeamRequest): UpdateDraftTeamResult =
  try {
    val team = apiPost(
      "/api/draft-team-update",
      apiJson.encodeToString(UpdateDraftTeamRequest.serializer(), request),
      TeamResponse.serializer(),
    )
    UpdateDraftTeamResult.Success(team)
  } catch (e: ApiException) {
    val field = TeamField.entries.firstOrNull { it.wireName == e.field }
    if (field != null) UpdateDraftTeamResult.Invalid(field, e.errorMessage)
    else UpdateDraftTeamResult.Failure(e.errorMessage)
  } catch (e: Throwable) {
    UpdateDraftTeamResult.Failure(e.message ?: UNREACHABLE_MESSAGE)
  }
